import React, { useMemo, useState, useEffect } from 'react';
import { Chip, createTheme, ThemeProvider } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { EditLabel } from '../../../../common/Form/EditLabel/EditLabel';
import { PreferenceLabel, PreferenceTypeTwo } from '../../../../Form/Preference';
import PreferenceTypeThree from '../../../../Form/Preference/PreferenceTypeThree/PreferenceTypeThree';
import styles from './JobPreferences.module.scss';
import { EditOutlinedIcon } from '../../../../common/Icons';
import AddEditFormModal from '../../../../common/ModalWindows/AddEditFormModal';
import BaseModal from '../../../../common/ModalWindows/BaseModal';
import { setFunctions, setIndustries } from '../../../../../actions/user';
import { maxMobileSize, validationMessages } from '../../../../../utils/constants';
import { usePrevious, useWindowSize } from '../../../../../utils/hooks';
import ActionsButton from '../../../../common/buttons/ActionsButton/ActionsButton';
import colors from '../../../../../scss/colors';

export function JobPreferences(props) {
  const { profile, jobLocation, updateJobLocation, updateContractType, updating } = props;

  const prevUpdating = usePrevious(updating);

  const size = useWindowSize();
  const { t } = useTranslation();
  const validationMessage = useMemo(() => validationMessages(t), [t]);

  const [location, setLocation] = useState({
    OpentoRemoteWork: jobLocation?.OpentoRemoteWork,
    OpenToRelocation: jobLocation?.OpenToRelocation
  });

  const [contractType, setContractType] = useState({
    ContractWork: profile?.ContractWork,
    PermanentWork: profile?.PermanentWork
  });

  const workLocation = [
    {
      id: 0,
      label: t('dashboard.profile.details.openremote'),
      option: 'OpentoRemoteWork',
      checked: jobLocation?.OpentoRemoteWork
    },
    {
      id: 1,
      label: t('dashboard.profile.details.openrelocation'),
      option: 'OpenToRelocation',
      checked: jobLocation?.OpenToRelocation
    }
  ];

  const contract = [
    {
      id: 0,
      label: t('dashboard.profile.details.contractwork'),
      option: 'ContractWork',
      selected: profile?.ContractWork
    },
    {
      id: 1,
      label: t('dashboard.profile.details.permenantwork'),
      option: 'PermanentWork',
      selected: profile?.PermanentWork
    }
  ];

  const dispatch = useDispatch();

  const [editIndustries, setEditIndustries] = useState(false);
  const [editFunctions, setEditFunctions] = useState(false);
  const [editContract, setEditContract] = useState(false);
  const [editWorkLocation, setEditWorkLocation] = useState(false);

  const theme = createTheme({
    palette: {
      primary: {
        main: colors.PRIMARY_BLUE
      }
    },
    color: colors.WHITE
  });

  const confirmIndustryHandler = (data) => {
    dispatch(
      setIndustries(
        profile.Id,
        data.industries.map((item) => ({ IndustryId: item.IndustryId })),
        data.industries
      )
    );
  };

  const confirmFunctionsHandler = (data) => {
    dispatch(
      setFunctions(
        profile.Id,
        data.functions.map((item) => ({ JobFunctionId: item.JobFunctionId })),
        data.functions
      )
    );
  };

  const confirmContractHandler = () => {
    if (!Object.values(contractType).filter((item) => item).length) {
      return;
    }
    updateContractType(contractType);
  };

  const confirmWorkLocation = () => {
    updateJobLocation(location);
  };

  useEffect(() => {
    if (prevUpdating === true && updating === false) {
      setEditContract(false);
      setEditWorkLocation(false);
      setEditIndustries(false);
      setEditFunctions(false);
    }
  }, [updating]);

  return (
    <div className={`mt-4 ${styles.JobPreferences}`}>
      <br />
      <EditLabel purpose={t('profile.builder.jobpreferences')} onClick={() => {}} />
      <br />

      <div className={styles.preferenceFields}>
        <PreferenceLabel
          primary={t('dashboard.profile.details.industries')}
          secondary={t('dashboard.profile.details.qualifiedindustries')}
        />
        <div className={styles.action}>
          <ActionsButton activityName="edit" clickHandler={() => setEditIndustries(true)} />
          {editIndustries && (
            <AddEditFormModal
              open
              key={profile?.UserIndustries}
              closeHandler={() => setEditIndustries(false)}
              editeMode
              initData={profile?.UserIndustries}
              confirmHandler={confirmIndustryHandler}
              block="industries"
              pending={updating}
            />
          )}
        </div>
        <div className={styles.chipsContainer}>
          {profile?.UserIndustries.map((item) => (
            <ThemeProvider key={item.Industry.Id} theme={theme}>
              <Chip className={styles.chip} label={item.Industry.Name} color="primary" variant="filled" />
            </ThemeProvider>
          ))}
        </div>
      </div>
      <hr className={styles.divider} />
      <br />
      <div className={styles.preferenceFields}>
        <PreferenceLabel
          primary={t('dashboard.profile.details.jobfunctions')}
          secondary={t('dashboard.profile.details.preferredfunctions')}
        />
        <div className={styles.action}>
          <ActionsButton activityName="edit" clickHandler={() => setEditFunctions(true)} />
          {editFunctions && (
            <AddEditFormModal
              open
              key={profile?.UserJobFunctions}
              closeHandler={() => setEditFunctions(false)}
              editeMode
              initData={profile?.UserJobFunctions}
              confirmHandler={confirmFunctionsHandler}
              block="functions"
              pending={updating}
            />
          )}
        </div>
        <div className={styles.chipsContainer}>
          {profile?.UserJobFunctions.map((item) => (
            <ThemeProvider key={item.JobFunction.Id} theme={theme}>
              <Chip className={styles.chip} label={item.JobFunction.Name} color="primary" variant="filled" />
            </ThemeProvider>
          ))}
        </div>
      </div>
      <hr className={styles.divider} />
      <br />

      <div className={styles.preferenceFields}>
        <PreferenceLabel primary={t('dashboard.profile.details.contracttypes')} />
        <div className={styles.action}>
          <ActionsButton activityName="edit" clickHandler={() => setEditContract(true)} />
          <BaseModal
            title={t('dashboard.modal.edit.contracttypes')}
            open={editContract}
            fullscreen={size.width < maxMobileSize}
            closeHandler={() => {
              setEditContract(false);
              setContractType({
                ContractWork: profile?.ContractWork,
                PermanentWork: profile?.PermanentWork
              });
            }}
            confirmHandler={confirmContractHandler}
            pending={updating}
          >
            <PreferenceTypeTwo
              fields={contract}
              state={contractType}
              setState={setContractType}
              errorMsg={validationMessage.TYPE_WORK_CHOOSE_ONE}
            />
          </BaseModal>
        </div>
        <div className={styles.chipsContainer}>
          <PreferenceTypeTwo fields={contract} state={contractType} setState={() => false} disabled />
        </div>
      </div>
      {/*
        <br  />

        <PreferenceLabel primary="Work Schedule  (select all that apply)" />
        <PreferenceTypeTwo fields={jobPreferenceData?.schedule} /> */}
      <hr className={styles.divider} />
      <br />

      <div className={styles.preferenceFields}>
        <PreferenceLabel primary={t('dashboard.profile.details.worklocation')} />
        <div className={styles.action}>
          <ActionsButton activityName="edit" clickHandler={() => setEditWorkLocation(true)} />
          <BaseModal
            title={t('dashboard.modal.edit.worklocation')}
            open={editWorkLocation}
            fullscreen={size.width < maxMobileSize}
            closeHandler={() => {
              setEditWorkLocation(false);
              setLocation({
                OpentoRemoteWork: jobLocation?.OpentoRemoteWork,
                OpenToRelocation: jobLocation?.OpenToRelocation
              });
            }}
            confirmHandler={confirmWorkLocation}
            pending={updating}
          >
            <PreferenceTypeThree location={location} setLocation={setLocation} fields={workLocation} />
          </BaseModal>
        </div>
        <div className={styles.chipsContainer}>
          <PreferenceTypeThree location={location} setLocation={() => false} fields={workLocation} disabled />
        </div>
      </div>
      <hr className={styles.divider} />
      <br />
    </div>
  );
}
